import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import range from 'lodash/range';
import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import PieChart from '@Components/Results/targets/PieChart';
import ChartConstants from '@Constants/ChartConstants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ColumnChart from '@Components/Results/targets/ColumnChart';
import Entries from '@Services/Entries';

import './TargetDetailsCharts.scss';

function TargetDetailsCharts({ metadata, target }) {
  const [areChartsReady, setAreChartsReady] = useState(false);
  const charts = useRef(null);

  const mapDataLabelsColors = useCallback((items, allColors, singleColor) => {
    const colors = [];
    const labels = [];
    const data = [];
    const pairs = [];

    (items ? [...items] : []).forEach((item, index) => {
      labels.push(item.label);
      data.push(item.size);
      pairs.push({ name: item.label, y: item.size });

      if (!singleColor) {
        if (item.noData) {
          colors.push(ChartConstants.colors.white);
        } else {
          colors.push(allColors[index]);
        }
      }
    });

    return {
      colors: singleColor ? ChartConstants.colors.barChartColors : colors,
      labels,
      data,
      pairs,
    };
  }, []);

  const preparePieChartItems = useCallback((items, allowedItemsCount) => {
    const noDataItemIndex = findIndex(items, (item) => item.noData);

    const itemsLength = noDataItemIndex !== -1 ? items.length - 1 : items.length;
    if (itemsLength <= allowedItemsCount) {
      return items;
    }

    const noDataItem = noDataItemIndex !== -1 ? items?.splice(noDataItemIndex, 1)[0] : null;

    (items || []).forEach((item, index) => (item.index = index));
    items = sortBy(items, (item) => item.size);

    const topItems = sortBy(items.splice(-(allowedItemsCount - 1), allowedItemsCount - 1), (item) => item.index);
    items = sortBy(items, (item) => item.index);

    const othersItem = { size: 0, label: 'Others: ', noData: false, other: true };
    items.forEach((item, index) => {
      othersItem.size += item.size;
      othersItem.label = [othersItem.label, item.label].join(index ? ', ' : '');
    });

    topItems.push(othersItem);

    if (noDataItem) {
      topItems.push(noDataItem);
    }

    return topItems;
  }, []);

  const preparePieData = useCallback(
    (items, colors) => {
      const correctedItems = preparePieChartItems(cloneDeep(items), colors.length);
      const data = mapDataLabelsColors(correctedItems, colors);

      data.options = cloneDeep(ChartConstants.pieChartOptions);
      return data;
    },
    [mapDataLabelsColors, preparePieChartItems]
  );

  const getBioassaysData = useCallback((items) => preparePieData(items, ChartConstants.colors.orange), [preparePieData]);

  const getCellLinesData = useCallback(
    (items) => {
      preparePieChartItems(cloneDeep(items), ChartConstants.colors.blue.length);
      return preparePieData(items, ChartConstants.colors.blue);
    },
    [preparePieChartItems, preparePieData]
  );

  const getLigandsData = useCallback(
    (items) => {
      const barsCount = 15;
      const barItems = range(barsCount).map(() => ({ label: '', size: 0 }));

      (items || []).forEach((item) => {
        if (item.noData) {
          barItems[barItems.length - 1] = item;
        } else {
          barItems[item.start] = item;
        }
      });

      const ligands = mapDataLabelsColors(barItems, null, ChartConstants.colors.mainOrange);
      ligands.options = ChartConstants.barChartOptions;

      return ligands;
    },
    [mapDataLabelsColors]
  );

  useEffect(() => {
    let isMounted = true;
    setAreChartsReady(false);

    Entries.getTargetsDetails(target.index, { ...metadata, databaseId: target.databaseId })
      ?.then((data) => {
        if (!isMounted) return;
        charts.current = {
          bioassays: getBioassaysData(data?.bioassays?.pieItems),
          cells: getCellLinesData(data?.cells?.pieItems),
          ligands: getLigandsData(data?.ligands?.histogramItems),
        };
        setAreChartsReady(true);
      })
      ?.catch((err) => {
        if (window.Logger && window.Logger.error) {
          window.Logger.error(err);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [metadata, target, getBioassaysData, getCellLinesData, getLigandsData]);

  return (
    <section className="target-details-charts">
      <h4 className="no-float">Distribution of bioactivities</h4>

      <section className="target-details-charts__pie-charts">
        <div className="target-details-charts__pie-chart over-bioassays">
          <h5 className="target-details-charts__title">Over bioassays</h5>
          {areChartsReady && charts.current?.bioassays && (
            <div className="target-details-charts__chart-row">
              <section className="target-details-charts__chart-section">
                <PieChart
                  data={charts?.current?.bioassays?.pairs}
                  colors={charts?.current?.bioassays?.colors}
                  options={charts?.current?.bioassays?.options}
                  chartTitle="Over bioassays"
                />
              </section>
              {/* try to use legend from highcharts here */}
              <aside className="target-details-charts__chart-legend">
                {map(charts?.current?.bioassays?.labels, (label, index) => (
                  <div className="target-details-charts__chart-legend__item" key={index}>
                    <div
                      className="target-details-charts__chart-legend__item-indicator"
                      style={{ backgroundColor: charts?.current?.bioassays?.colors?.[index] }}
                    />
                    <p className="target-details-charts__chart-legend__item-text">{label}</p>
                  </div>
                ))}
              </aside>
            </div>
          )}
        </div>
        <div className="target-details-charts__pie-chart over-cell-lines">
          <h5 className="target-details-charts__title">Over cell lines</h5>
          {areChartsReady && charts.current?.cells && (
            <div className="target-details-charts__chart-row">
              <section className="target-details-charts__chart-section">
                <PieChart
                  data={charts.current?.cells?.pairs}
                  colors={charts.current?.cells?.colors}
                  options={charts.current?.cells?.options}
                  chartTitle="Over cell lines"
                />
              </section>
              <aside className="target-details-charts__chart-legend">
                {map(charts.current?.cells?.labels, (label, index) => (
                  <div className="target-details-charts__chart-legend__item" key={index}>
                    <div
                      className="target-details-charts__chart-legend__item-indicator"
                      style={{ backgroundColor: charts.current?.cells?.colors?.[index] }}
                    />
                    <p className="target-details-charts__chart-legend__item-text">{label}</p>
                  </div>
                ))}
              </aside>
            </div>
          )}
        </div>
      </section>

      {areChartsReady && charts.current?.ligands && (
        <section className="target-details-charts__histogram-charts">
          <div className="target-details-charts__histogram-chart over-ligands">
            <h5 className="target-details-charts__title">Over ligands</h5>
            <section>
              <div className="rx-chart--wrapper rx-chart-custom-tooltip">
                <ColumnChart
                  height="20%"
                  values={charts.current?.ligands.data}
                  categories={charts.current?.ligands.labels}
                  colors={charts.current?.ligands.colors}
                  options={charts.current?.ligands.options}
                  yAxis="Compound count"
                />
              </div>
            </section>
          </div>
        </section>
      )}
    </section>
  );
}

TargetDetailsCharts.propTypes = {
  metadata: MetadataPropTypesShape,
  target: PropTypes.object.isRequired,
};

export default TargetDetailsCharts;
